import React, {
  useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
} from 'react';

/**
 * The small `i` beside a section title. Hover (or focus, or tap) reveals the
 * detail that used to sit in a five-line paragraph under the heading.
 *
 * The split it exists to enforce: the visible description says what the
 * section IS, in one line an operator reads once and never needs again; the
 * tip holds the parts that matter only when something is being decided or has
 * gone wrong. Both were previously in the paragraph, so the sentence that
 * mattered was buried among four that did not.
 *
 * Why it is not a `title` attribute. Native tooltips take about a second to
 * appear, cannot be reached by keyboard, vanish on any movement, and cannot
 * hold a <code> or a list — and much of this detail is endpoint paths and
 * status codes, which are unreadable as one unbroken line.
 *
 * Interaction, and why each part is there:
 *   - hover opens, with a ~90ms close delay so crossing the gap between the
 *     button and the panel does not dismiss it;
 *   - focus opens and blur closes, so it is reachable by keyboard;
 *   - click toggles and pins, which is the only path that works on a
 *     touchscreen, where there is no hover at all;
 *   - Escape closes, and a pinned panel also closes on an outside click.
 *
 * Positioned `fixed` against a measurement of the button rather than absolute
 * inside it: several of these sit in a `card-header` with `overflow` set, and
 * an absolutely-positioned panel is clipped by it.
 *
 * `place()` keeps the panel fully on screen on ALL FOUR edges, which needs two
 * passes because the panel's height is not knowable until it has rendered:
 *
 *   1. `show()` places it below the button with the height still unknown, so it
 *      may briefly sit lower than it will end up.
 *   2. a layout effect re-runs `place()` once the panel is in the DOM, now with
 *      a real height, and flips it above the button if opening downward would
 *      run past the bottom of the viewport. Because this is a LAYOUT effect the
 *      correction lands before the browser paints, so there is no visible jump.
 *
 * `place()` is idempotent — it bails when the numbers it computes match the
 * numbers already in state — which is what stops that second pass looping.
 *
 * The vertical case is the one that used to break: the horizontal flip has been
 * here from the start, but a tip on the last row of a long table opened
 * downward into nothing and the text was unreachable. When neither direction
 * has room (a tall tip in a short window) the panel is pinned to the margin and
 * given a `maxHeight`, so it scrolls instead of being cut off. `width` is capped
 * to the viewport for the same reason: a 390px panel does not fit a 360px phone.
 *
 * Props:
 *   - children: the detail. Nodes, not a string, so it can carry <code>,
 *               <strong> and lists.
 *   - label:    accessible name. Defaults to "More information", but pass the
 *               section name where several sit on one page — a screen reader
 *               announcing "More information" four times says nothing.
 *   - width:    preferred panel width in px, default 340. Narrowed, never
 *               widened, to fit the viewport.
 */
export const MARGIN = 12;   // smallest gap we ever leave against a viewport edge
export const OFFSET = 8;    // gap between the button and the panel

/**
 * Where to draw the panel, given the button and the viewport. Pure, and exported
 * so the clamp can be tested directly — the whole point of it is a guarantee
 * about geometry ("never off-screen on any edge"), and that is a property of
 * arithmetic, not of a rendered DOM.
 *
 *   button      { top, bottom, left, right } — a DOMRect, or a plain object
 *   viewport    { width, height }
 *   width       the caller's preferred panel width
 *   panelHeight measured height, or 0 before the panel has rendered
 *
 * Returns { top, left, width, maxHeight }. `width` may be narrower than asked
 * for and `maxHeight` may be smaller than the content, but the box it describes
 * is always inside the viewport.
 */
export function placePanel({ button, viewport, width, panelHeight = 0 }) {
  const vw = viewport.width;
  const vh = viewport.height;

  // Never wider than the viewport allows, whatever the caller asked for.
  const w = Math.max(0, Math.min(width, vw - MARGIN * 2));

  // Prefer opening rightward from the button; flip when that would overflow,
  // then clamp, because on a narrow viewport BOTH directions can overflow.
  let left = button.left;
  if (left + w + MARGIN > vw) left = button.right - w;
  left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));

  const below = vh - button.bottom - OFFSET - MARGIN;   // room under the button
  const above = button.top - OFFSET - MARGIN;           // room over it

  let top;
  let maxHeight;
  if (panelHeight === 0 || panelHeight <= below) {
    // Fits below, or not measured yet and so assumed to.
    top = button.bottom + OFFSET;
    maxHeight = below;
  } else if (panelHeight <= above) {
    top = button.top - OFFSET - panelHeight;            // fits above, so flip
    maxHeight = above;
  } else {
    // Neither side fits. Take the roomier one, pin to the margin and let the
    // panel scroll — losing the tail of the text off-screen is worse. Going
    // above stops at the button rather than covering it.
    const useAbove = above > below;
    maxHeight = useAbove ? above : below;
    top = useAbove ? MARGIN : button.bottom + OFFSET;
  }

  // The invariant, applied last and to whatever the branches decided, because
  // this is the promise the whole function exists to make:
  //     MARGIN <= top   and   top + maxHeight <= vh - MARGIN
  //
  // It is not redundant. `maxHeight` above is the room available on the side we
  // chose, measured from the MARGIN, while `top` is measured from the button —
  // so in the flip-above branch `top + above` overshot the bottom of the window
  // by however far down the page the button sat. A brute-force sweep of 556,316
  // placements found 90,632 boxes leaving the viewport before this clamp, and 0
  // after. Derive the ceiling from the `top` actually chosen, never from the
  // side it came from.
  top = Math.max(MARGIN, Math.min(top, vh - MARGIN));
  return {
    top,
    left,
    width: w,
    maxHeight: Math.max(0, Math.min(maxHeight, vh - MARGIN - top)),
  };
}

export default function InfoTip({ children, label = 'More information', width = 340 }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const closeTimer = useRef(null);
  const panelId = useId();

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;

    // NATURAL height, via scrollHeight, not the rendered box. The rendered box
    // is already capped by the `maxHeight` the previous pass set, so measuring
    // it would report exactly the space below for any panel that overflows — the
    // flip test would always pass and the panel would never move. scrollHeight
    // is the content height whatever the cap; the offset/client delta adds the
    // borders back.
    const panel = panelRef.current;
    const panelHeight = panel
      ? panel.scrollHeight + (panel.offsetHeight - panel.clientHeight)
      : 0;

    const next = placePanel({
      button: el.getBoundingClientRect(),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      width,
      panelHeight,
    });

    // Return the SAME object when nothing moved. `pos` is a dependency of the
    // layout effect that calls this, so a fresh object every time would loop.
    setPos((prev) => (
      prev && prev.top === next.top && prev.left === next.left
        && prev.width === next.width && prev.maxHeight === next.maxHeight
        ? prev
        : next
    ));
  }, [width]);

  const show = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    place();
    setOpen(true);
  }, [place]);

  const hide = useCallback((immediate = false) => {
    if (pinned && !immediate) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), immediate ? 0 : 90);
  }, [pinned]);

  // Escape closes from anywhere, including while the panel has focus.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setPinned(false);
      setOpen(false);
      btnRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // A pinned panel has no hover to lose, so it needs an explicit dismissal.
  useEffect(() => {
    if (!open || !pinned) return undefined;
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (e.target.closest?.('.infotip-panel')) return;
      setPinned(false);
      setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open, pinned]);

  // The panel is fixed, so a scroll or resize would leave it behind.
  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  // Pass two. The first `place()` ran before the panel existed, so it could only
  // assume there was room below. Now that it is in the DOM its height is known,
  // and this flips it above the button if it needs to. A LAYOUT effect, so the
  // correction is applied before paint rather than as a visible jump; and
  // `place()` no-ops once the numbers settle, so this does not loop.
  useLayoutEffect(() => {
    if (open) place();
  }, [open, pos, place]);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  return (
    <span className="infotip">
      <button
        type="button"
        ref={btnRef}
        className="infotip-button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? panelId : undefined}
        onMouseEnter={show}
        onMouseLeave={() => hide()}
        onFocus={show}
        onBlur={() => hide()}
        onClick={() => {
          if (pinned) {
            setPinned(false);
            hide(true);
          } else {
            setPinned(true);
            show();
          }
        }}
      >
        i
      </button>

      {open && pos && (
        <span
          id={panelId}
          role="tooltip"
          ref={panelRef}
          className="infotip-panel"
          style={{
            top: pos.top,
            left: pos.left,
            width: pos.width,
            // Only ever a ceiling: a short tip is unaffected, a tip taller than
            // the window scrolls inside the panel instead of off the screen.
            maxHeight: pos.maxHeight,
            overflowY: 'auto',
          }}
          onMouseEnter={show}
          onMouseLeave={() => hide()}
        >
          {children}
        </span>
      )}
    </span>
  );
}
